/**
 * Portal Component
 *
 * Teleports DOM content between mobile and desktop locations.
 *
 * @component portal-component
 * @extends HTMLElement
 *
 * @description
 * Moves content from a source element (with portal:source attribute) into this
 * container when the container enters the viewport. Used to share filter UI
 * between mobile dialog and desktop dropdowns.
 *
 * **How it works:**
 * 1. Desktop dropdown has <portal-component data-source="departments">
 * 2. Mobile dialog has <div portal:source="departments">Filter UI</div>
 * 3. When dropdown enters viewport → content moves from mobile to desktop
 * 4. When dropdown leaves viewport → content moves back to mobile
 * 5. On 'portal:recall' event → content refreshes (re-moves to resync)
 *
 * @behavior
 * - Uses MutationObserver on the nearest ancestor with [aria-hidden] to detect
 *   when the portal becomes visible (e.g. a dropdown opening). Falls back to
 *   IntersectionObserver if no aria-hidden ancestor is found.
 * - Preserves scroll positions during content moves (prevents UI jump)
 * - Supports fallback content (shown if source doesn't exist)
 * - Only one portal:source per data-source name (1:1 relationship)
 *
 * **Why MutationObserver over IntersectionObserver?**
 * iOS Safari's IntersectionObserver does not reliably fire when the observed
 * element transitions from display:none to display:block via an ancestor's
 * attribute change. This leaves desktop filter dropdowns empty on iPad.
 * Watching aria-hidden directly avoids that quirk.
 *
 * @architecture
 * Mobile-First Pattern:
 * ```
 * Mobile Dialog (always in DOM):
 * <dialog>
 *   <div portal:source="departments"> <!-- SOURCE -->
 *     <!-- Filter checkboxes -->
 *   </div>
 * </dialog>
 *
 * Desktop Dropdown (consumes source when visible):
 * <dropdown-component>
 *   <portal-component data-source="departments"> <!-- TARGET -->
 *     <!-- Content moves here when dropdown opens -->
 *   </portal-component>
 * </dropdown-component>
 * ```
 *
 * @attributes
 * @attr {string} data-source - Name matching portal:source attribute on source element
 *
 * @listens portal:recall - Refreshes portal (moves content out and back in)
 *
 * @example
 * Typical usage in filter system:
 * ```liquid
 * <!-- Desktop filter bar (TARGET) -->
 * <div data-filter-bar>
 *   <dropdown-component>
 *     <button ref="trigger">Departments</button>
 *     <div ref="content">
 *       <portal-component data-source="departments"></portal-component>
 *     </div>
 *   </dropdown-component>
 * </div>
 *
 * <!-- Mobile filter dialog (SOURCE) -->
 * <dialog-component id="filter-modal">
 *   {% render 'filter-mobile', filter_type: 'departments' %}
 * </dialog-component>
 *
 * <!-- snippets/filter-mobile.liquid -->
 * <div portal:source="departments">
 *   <!-- Filter checkboxes -->
 * </div>
 * ```
 *
 * @scroll_preservation
 * When content is refreshed via portal:recall, the component:
 * 1. Records scroll positions of all scrollable ancestors
 * 2. Moves content out (hidePortal) and back in (showPortal)
 * 3. Restores scroll positions in same requestAnimationFrame
 * This prevents the dropdown from scrolling to top when filters update.
 *
 * @extension_points
 * To add a new portal:
 * 1. Choose a unique data-source name (e.g., "colors")
 * 2. Add <portal-component data-source="colors"> in desktop dropdown
 * 3. Add <div portal:source="colors"> in mobile dialog
 * 4. Portal system handles the rest automatically
 *
 * @dependencies
 * - collection-filter.js - Dispatches 'portal:recall' after AJAX updates
 */
class PortalComponent extends HTMLElement {
  #observer = null;
  #source = null;
  #parent = null;
  #sibling = null;
  #portaled = false;
  #fallback = null;

  connectedCallback() {
    const attr = this.getAttribute('data-source');

    if (!attr) {
      console.warn('Portal component missing data-source attribute');
      return;
    }

    // Store fallback content (anything initially inside the portal component)
    if (this.childNodes.length > 0) {
      this.#fallback = document.createDocumentFragment();
      while (this.firstChild) {
        this.#fallback.appendChild(this.firstChild);
      }
    }

    this.#source = document.querySelector(`[portal\\:source="${attr}"]`);

    if (!this.#source) {
      console.warn(`Could not find element with attribute [portal:source="${attr}"]`);
      return;
    }

    const visibilityContainer = this.closest('[aria-hidden]');

    if (visibilityContainer) {
      const syncVisibility = () => {
        if (visibilityContainer.getAttribute('aria-hidden') === 'false') {
          this.showPortal();
        } else {
          this.hidePortal();
        }
      };

      this.#observer = new MutationObserver(syncVisibility);
      this.#observer.observe(visibilityContainer, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });

      syncVisibility();
    } else {
      this.#observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.showPortal();
        } else {
          this.hidePortal();
        }
      });

      this.#observer.observe(this);
    }

    document.addEventListener('portal:recall', () => this.handleRecall());
  }

  showPortal() {
    if (this.#portaled) return;

    const attr = this.getAttribute('data-source');
    this.#source = document.querySelector(`[portal\\:source="${attr}"]`);

    if (!this.#source) return;

    // Clear portal (removes fallback if present)
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }

    // Discard fallback permanently after first use
    this.#fallback = null;

    this.#parent = this.#source.parentNode;
    this.#sibling = this.#source.nextSibling;

    this.appendChild(this.#source);

    this.#portaled = true;
  }

  hidePortal() {
    if (!this.#portaled || !this.#parent) return;

    if (this.#parent === this.#sibling?.parentNode) {
      this.#parent.insertBefore(this.#source, this.#sibling);
    } else {
      this.#parent.appendChild(this.#source);
    }

    this.#portaled = false;
  }

  handleRecall() {
    if (!this.#portaled) return;

    // Store scroll positions of scrollable ancestor elements
    const scrollableAncestors = [];
    let element = this.parentElement;

    while (element && element !== document.documentElement) {
      // Check if element is scrollable (has overflow-auto or similar)
      const isScrollable = element.classList.contains('overflow-auto') ||
                          element.scrollHeight > element.clientHeight ||
                          element.scrollWidth > element.clientWidth;

      if (isScrollable && (element.scrollTop > 0 || element.scrollLeft > 0)) {
        scrollableAncestors.push({
          element: element,
          scrollTop: element.scrollTop,
          scrollLeft: element.scrollLeft
        });
      }

      element = element.parentElement;
    }

    this.hidePortal();

    window.requestAnimationFrame(() => {
      this.showPortal();

      // Restore scroll positions synchronously in same RAF to prevent flicker
      scrollableAncestors.forEach(({ element, scrollTop, scrollLeft }) => {
        element.scrollTop = scrollTop;
        element.scrollLeft = scrollLeft;
      });
    });
  }

  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
    }

    document.removeEventListener('portal:recall', () => this.handleRecall());
  }
}

if (!customElements.get('portal-component')) {
  customElements.define('portal-component', PortalComponent);
}
